import { useCallback, useEffect, useRef, useState } from 'react';
import { TEntity } from '../../models/entity';
import { GridRowModel, GridRowsProp, GridRowTreeNodeConfig } from '@mui/x-data-grid-pro';
import {
    TreeModelInnstillingWrapper,
    TreeSettingsModel,
} from '../../models/treeModelInnstillingWrapper';
import { meQueryApiService } from '../../api/rtk-queries/extensions/meQueryApiService';
import { useAppDispatch } from '../store';

export interface TreeRowsBuilderProps<T extends TEntity> {
    parentFieldID: keyof T;
    propertyId: keyof T;
    parentPropertyId?: keyof T;
    rows: T[];
    defaultExpandAll?: boolean;
    gridId: string;
    saveInnstillings?: boolean;
    getAllNode?: () => Promise<T[]>;
    getChildrenItems: (id: any, expanded: boolean) => Promise<T[]>;
}
export interface TreeFetchingUtils {
    refetch: () => void;
    isSuccessUpdated: boolean;
}
export function useTreeFetchingUtils({ refetch }: TreeFetchingUtils) {
    const dispatch = useAppDispatch();
    useEffect(() => {
        refetch();
    }, [dispatch, refetch]);
}

export function useTreeRowsBuilder<T extends TEntity>(
    props: TreeRowsBuilderProps<T>
) {
    const rowsIntermediateForBuilding = useRef<GridRowsProp>([]);
    const {
        parentFieldID,
        propertyId,
        rows: initialRows,
        getChildrenItems,
        saveInnstillings = true,
    } = props;
    const parentPropertyId = props.parentPropertyId ?? propertyId;
    const [expandAll, setExpandedAll] = useState(false);
    const [treeRows, setTreeRows] = useState<GridRowsProp>([]);
    const [expandedEntity, setExpandedEntity] = useState<TreeSettingsModel>();
    const [setTreesetting] = meQueryApiService.useSetTreesettingMutation();
    const dispatch = useAppDispatch();
    const [isFetching, setIsFetching] = useState(false);
    const [rows, setRows] = useState<T[]>([]);

    const isGroupExpandedByDefault = useCallback((node: GridRowTreeNodeConfig) => {
        return expandedEntity?.treeModelInnstillingWrappers?.some(i => i.entityId === node.id.toString()) ?? false;
    }, [expandedEntity?.treeModelInnstillingWrappers]);

    useEffect(() => {
        setRows(initialRows);
    }, [initialRows]);


    useEffect(() => {
        async function fetchMeSetting() {
            const { data } = await dispatch(
                meQueryApiService.endpoints.hentTreesetting.initiate(props.gridId)
            );
            if (data) {
                if (data.expandAll) setExpandedAll(true);
                setExpandedEntity(data);
            }
        }
        fetchMeSetting();
    }, [dispatch, props.gridId]);

    const buildRows = useCallback( // need to make path for each row
        (item: T | null, existingRows = []) => {
            if (!item) { // start
                rowsIntermediateForBuilding.current = [];
                let firstNodes = rows.filter(i => !i[parentFieldID]);
                firstNodes.forEach(i => buildRows(i));
                return rowsIntermediateForBuilding.current;
            } else {
                let path: string[] = (item[parentPropertyId] as unknown as string)
                    ? [(item[parentPropertyId] as unknown as string).toString()]
                    : [];
                if (item[parentFieldID]) {
                    let parentNode = rowsIntermediateForBuilding.current.find(
                        i => (i as T)[parentPropertyId] === (item[parentFieldID] as unknown as string));
                    if (parentNode) {
                        path = [...parentNode.path, ...path];
                    }
                }

                let childrenNodes = rows.filter(
                    i => (i[parentFieldID] as unknown as string)?.toString() === (item.id as unknown as string)?.toString()
                ); // get children for item
                rowsIntermediateForBuilding.current = [...rowsIntermediateForBuilding.current, { ...item, path: path },];

                if (childrenNodes) {
                    childrenNodes?.forEach(chn => buildRows(chn));
                }
                return rowsIntermediateForBuilding.current;
            }
        },
        [parentFieldID, rows, parentPropertyId]
    );

    const getExpandedChildrenForItem = useCallback(
        (item: T) => {
            rowsIntermediateForBuilding.current = [];
            buildRows(item);
            return expandedEntity?.treeModelInnstillingWrappers.filter(j =>
                rowsIntermediateForBuilding.current.findIndex(
                    i => (i[propertyId.toString()] as unknown as string).toString() === j.entityId
                ) > -1
            );
        },
        [buildRows, expandedEntity?.treeModelInnstillingWrappers, propertyId]
    );

    const getTreeDataPath = useCallback((row: GridRowModel) => {
        return row.path;
    }, []);

    useEffect(() => {
        setTreeRows(buildRows(null));
    }, [buildRows]);

    const expandChildren = useCallback(
        async (parentsId: string | number, parentServerId: string | number) => {
            let hasChildren = rows.some(row => row[parentFieldID] === parentsId);

            let newTreeSetting = {
                id: props.gridId,
                expandAll: false,
                treeModelInnstillingWrappers: [
                    ...(expandedEntity?.treeModelInnstillingWrappers ?? []),
                    {
                        entityId: parentsId?.toString(),
                        isExpanded: true,
                    } as TreeModelInnstillingWrapper,
                ],
            };
            if (saveInnstillings)
                setTreesetting(newTreeSetting);
            setExpandedEntity(newTreeSetting);
            if (hasChildren) return;

            try {
                setIsFetching(true);
    
                const children = await getChildrenItems(parentServerId, false);
                setRows(prev => [...prev, ...children]);
            } finally {
                setIsFetching(false);
            }
        },
        [
            getChildrenItems,
            parentFieldID,
            rows,
            saveInnstillings,
            setTreesetting,
            expandedEntity?.treeModelInnstillingWrappers,
            props.gridId,
        ]
    );

    const hideChildren = useCallback( // just remove this node from treeModelInnstillingWrappers
        (parentsId: string | number) => {
            let newTreeSetting = {
                id: props.gridId,
                expandAll: false,
                treeModelInnstillingWrappers: [
                    ...(expandedEntity?.treeModelInnstillingWrappers?.filter(
                        exp => exp.entityId !== parentsId?.toString()
                    ) ?? []),
                ],
            };

            setExpandedAll(false);
            setIsFetching(true);
            if (saveInnstillings)
                setTreesetting(newTreeSetting);
            setExpandedEntity(newTreeSetting);
            setIsFetching(false);
        },
        [
            setTreesetting,
            saveInnstillings,
            expandedEntity?.treeModelInnstillingWrappers,
            props.gridId,
        ]
    );

    const handleHideAll = useCallback(() => { // hide all just remove ids from db
        let newTreeSetting = {
            id: props.gridId,
            expandAll: false,
            treeModelInnstillingWrappers: [],
        };
        setIsFetching(true);
        if (saveInnstillings)
            setTreesetting(newTreeSetting);
        setExpandedEntity(newTreeSetting);
        setExpandedAll(false);
        setIsFetching(false);
    }, [props.gridId, saveInnstillings, setTreesetting]);

    const handleRefresh = useCallback(async () => {
        if (!props.getAllNode)
            return;
        let allItems = await props.getAllNode();
        setRows(allItems);
    }, [props]);

    const handleExpandChildrenForItem = useCallback(
        async (node: T) => {
            let parentsId = node[propertyId];
            setIsFetching(true);

            await getChildrenItems(parentsId as string | number, true).then((val: T[]) => { // get all levels
                let newRow = [...rows];
                val.forEach(v => {   //check on exist item in the current rows if exist skip(should be not duplicate)
                    var isFetched = newRow.findIndex(i => i[propertyId] === v[propertyId]);
                    if (isFetched > -1) return;
                    newRow = [...newRow, v];
                });

                setRows(newRow);
                let newTreeSetting = {
                    id: props.gridId,
                    expandAll: false,
                    treeModelInnstillingWrappers: [
                        ...(expandedEntity?.treeModelInnstillingWrappers ?? []),
                        { entityId: (parentsId as any).toString(), isExpanded: true, isSelected: true }
                    ],
                }; // make new settings
                
                val.filter(i => i['hasChildren' as unknown as keyof T]).forEach( // insert children levels as expanded
                    v => {
                        var isExpanded = newTreeSetting?.treeModelInnstillingWrappers
                            .findIndex(i => i.entityId === (v[propertyId] as unknown as string).toString()) ?? 0;
                        if (isExpanded > -1) return;
                        newTreeSetting.treeModelInnstillingWrappers = [
                            ...newTreeSetting.treeModelInnstillingWrappers,
                            {
                                entityId: (v[propertyId] as unknown as string)?.toString(),
                                isExpanded: true,
                            } as TreeModelInnstillingWrapper,
                        ];
                    }
                );
                if (saveInnstillings)
                    setTreesetting(newTreeSetting);

                setExpandedEntity(newTreeSetting);
                setIsFetching(false);
            });
        },
        [getChildrenItems, rows, saveInnstillings, propertyId, setTreesetting, expandedEntity?.treeModelInnstillingWrappers, props.gridId]
    );

    const handleNodeLessCallback = useCallback(
        (node: T) => {
            let allChildren = treeRows.filter(i =>
                (i.path as unknown as string[]).some(j => j === (node[propertyId] as unknown as string).toString()));// get all childrens for node
            let newTreeSetting = {
                id: props.gridId,
                expandAll: false,
                treeModelInnstillingWrappers: [// make new settings where filter childrens and current node 
                    ...(expandedEntity?.treeModelInnstillingWrappers?.filter(
                        i => !allChildren.some(j => (j[propertyId.toString()] as unknown as string)?.toString() === i.entityId)
                    ) ?? []),
                ],
            };
            if (saveInnstillings)
                setTreesetting(newTreeSetting);
            setExpandedEntity(newTreeSetting);
        },
        [
            expandedEntity?.treeModelInnstillingWrappers,
            props.gridId,
            propertyId,
            saveInnstillings,
            setTreesetting,
            treeRows,
        ]
    );
    const handleExpandAllNodeCallback = useCallback(async () => { // just load all and save all in the setting
        setIsFetching(true);
        await props.getAllNode!().then((val: T[]) => {
            if (val) {
                setRows([...val]);
                let newTreesetting = {
                    id: props.gridId,
                    expandAll: true,
                    treeModelInnstillingWrappers: [
                        ...val
                            .filter(i => i['hasChildren' as unknown as keyof T] as unknown as boolean)
                            .map(i => {
                                return {
                                    entityId: (
                                        i[propertyId] as unknown as string
                                    )?.toString(),
                                    isExpanded: true,
                                } as TreeModelInnstillingWrapper;
                            }),
                    ],
                };
                if (saveInnstillings)
                    setTreesetting(newTreesetting);
                setExpandedEntity(newTreesetting);
                setExpandedAll(false);
                setIsFetching(false);
            }
        });
    }, [propertyId, props.getAllNode, props.gridId, saveInnstillings, setTreesetting]);

    useEffect(() => {
        if (!props.defaultExpandAll)
            return;
        handleExpandAllNodeCallback();
    }, [props.defaultExpandAll, handleExpandAllNodeCallback, initialRows, propertyId, props.gridId]);

    return {
        buildRows,
        getTreeDataPath,
        expandChildren,
        isGroupExpandedByDefault,
        hideChildren,
        handleHideAll,
        saveInnstillings,
        handleExpandChildrenForItem,
        handleNodeLessCallback,
        getExpandedChildrenForItem,
        handleExpandAllNodeCallback,
        expandedEntity,
        handleRefresh,
        isFetching,
        expandAll,
        treeRows,
    };
}

